<template>
  <div class="platform-users-manager" :class="{ 'dark-theme': isDarkTheme }">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-left">
        <h1 class="page-title">Platform Users</h1>
        <p class="page-subtitle">Manage platform administrators and trainers</p>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        @click="tab.value !== 'platform_trainer' && (activeTab = tab.value)"
        class="tab-btn"
        :class="{
          active: activeTab === tab.value,
          'blurred-tab': tab.value === 'platform_trainer'
        }"
      >
        <i :class="tab.icon"></i>
        {{ tab.label }}
        <span v-if="getTabCount(tab.value)" class="tab-count">
          {{ getTabCount(tab.value) }}
        </span>
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      <span>Loading users...</span>
    </div>

    <!-- Users List -->
    <div v-else-if="filteredUsers.length > 0" class="users-list">
      <div v-for="user in filteredUsers" :key="user._id" class="user-card">
        <div class="user-info">
          <div class="user-avatar" :class="user.role">
            {{ getInitials(user.name || user.email) }}
          </div>
          <div class="user-details">
            <h3 class="user-name">{{ user.name || 'Unnamed User' }}</h3>
            <span class="user-email">{{ user.email }}</span>
            <div class="user-meta">
              <span class="role-badge" :class="user.role">{{ user.role }}</span>
              <span class="last-login" v-if="user.lastLoginAt">
                <i class="pi pi-clock"></i>
                Last login: {{ formatDate(user.lastLoginAt) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Trainer Assignments (for trainers) -->
        <div
          v-if="user.role === 'platform_trainer' && user.assignments?.length"
          class="assignments blurred-section"
        >
          <div class="blur-overlay-small">
            <i class="pi pi-lock"></i>
            <span>Coming Soon</span>
          </div>
          <span class="assignments-label">Assigned Tenants:</span>
          <div class="assignment-tags">
            <span
              v-for="assignment in user.assignments"
              :key="assignment.tenantSlug"
              class="assignment-tag"
            >
              {{ assignment.tenantSlug }}
            </span>
          </div>
        </div>

        <!-- Actions -->
        <div class="user-actions">
          <button
            v-if="user.role === 'platform_trainer'"
            @click="manageAssignments(user)"
            class="action-btn blurred-button"
            title="Coming Soon"
            disabled
          >
            <i class="pi pi-building"></i>
          </button>
          <button
            v-if="user.isActive !== false"
            @click="deactivateUser(user)"
            class="action-btn danger"
            title="Deactivate"
          >
            <i class="pi pi-ban"></i>
          </button>
          <button v-else @click="activateUser(user)" class="action-btn success" title="Activate">
            <i class="pi pi-check-circle"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <i class="pi pi-users"></i>
      </div>
      <h3>No {{ activeTab === 'all' ? 'Platform Users' : activeTab + 's' }} Found</h3>
      <p>Platform users can register through the self-registration flow.</p>
    </div>

    <!-- Assignments Modal -->
    <div v-if="showAssignments" class="modal-overlay" @click.self="showAssignments = false">
      <div class="modal modal-large blurred-section">
        <div class="blur-overlay">
          <i class="pi pi-lock"></i>
          <span>Coming Soon</span>
          <p>Trainer functionality is not yet available</p>
        </div>
        <div class="modal-header">
          <h2>Manage Tenant Assignments</h2>
          <button @click="showAssignments = false" class="modal-close">
            <i class="pi pi-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="assignments-grid">
            <label
              v-for="tenant in allTenants"
              :key="tenant.slug"
              class="tenant-checkbox"
              :class="{ checked: isAssigned(tenant.slug) }"
            >
              <input type="checkbox" :value="tenant.slug" v-model="selectedAssignments" />
              <div class="tenant-info">
                <span class="tenant-name">{{ tenant.name }}</span>
                <code class="tenant-slug">{{ tenant.slug }}</code>
              </div>
            </label>
          </div>
          <div class="modal-actions">
            <button @click="showAssignments = false" class="btn-secondary">Cancel</button>
            <button @click="saveAssignments" class="btn-primary" :disabled="savingAssignments">
              <i v-if="savingAssignments" class="pi pi-spin pi-spinner"></i>
              {{ savingAssignments ? 'Saving...' : 'Save Assignments' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { platformApi } from '@/services/platformApi';

export default {
  name: 'PlatformUsersManager',
  props: {
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      users: [],
      allTenants: [],
      activeTab: 'all',
      showAssignments: false,
      selectedUser: null,
      selectedAssignments: [],
      savingAssignments: false,
      tabs: [
        { value: 'all', label: 'All Users', icon: 'pi pi-users' },
        { value: 'platform_admin', label: 'Admins', icon: 'pi pi-shield' },
        { value: 'platform_trainer', label: 'Trainers', icon: 'pi pi-user' },
      ],
    };
  },
  computed: {
    filteredUsers() {
      if (this.activeTab === 'all') return this.users;
      return this.users.filter((user) => user.role === this.activeTab);
    },
  },
  mounted() {
    this.loadUsers();
    this.loadTenants();
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      try {
        const users = await platformApi.getUsers();
        this.users = users || [];
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        this.loading = false;
      }
    },
    async loadTenants() {
      try {
        const tenants = await platformApi.getTenants();
        this.allTenants = Array.isArray(tenants) ? tenants : [];
      } catch (error) {
        console.error('Failed to load tenants:', error);
      }
    },
    getTabCount(tab) {
      if (tab === 'all') return this.users.length;
      return this.users.filter((u) => u.role === tab).length;
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    async deactivateUser(user) {
      // eslint-disable-next-line no-alert
      if (!window.confirm(`Deactivate ${user.email}?`)) return;
      try {
        await platformApi.deactivateUser(user._id);
        this.loadUsers();
      } catch (error) {
        console.error('Failed to deactivate user:', error);
      }
    },
    async activateUser(user) {
      try {
        await platformApi.activateUser(user._id);
        this.loadUsers();
      } catch (error) {
        console.error('Failed to activate user:', error);
      }
    },
    manageAssignments(user) {
      this.selectedUser = user;
      this.selectedAssignments = (user.assignments || []).map((a) => a.tenantSlug);
      this.showAssignments = true;
    },
    isAssigned(slug) {
      return this.selectedAssignments.includes(slug);
    },
    async saveAssignments() {
      this.savingAssignments = true;
      try {
        await platformApi.updateUserAssignments(this.selectedUser._id, this.selectedAssignments);
        this.showAssignments = false;
        this.loadUsers();
      } catch (error) {
        console.error('Failed to update assignments:', error);
      } finally {
        this.savingAssignments = false;
      }
    },
  },
};
</script>

<style scoped>
/* Light Theme (Default) */
.platform-users-manager {
  --text-primary: #1E2019;
  --text-secondary: #666666;
  --text-muted: #64748b;
  --bg-card: #ffffff;
  --bg-card-alt: #f8f8f8;
  --border-color: #e7e7e7;
  --accent-border: rgba(228, 130, 26, 0.2);

  max-width: 1200px;
  margin: 0 auto;
  transition: all 0.3s ease;
}

/* Dark Theme */
.platform-users-manager.dark-theme {
  --text-primary: #f5f5f5;
  --text-secondary: #a8a8a0;
  --text-muted: #777777;
  --bg-card: #2a2a24;
  --bg-card-alt: #353530;
  --border-color: rgba(228, 130, 26, 0.2);
  --accent-border: rgba(228, 130, 26, 0.3);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.page-subtitle {
  color: var(--text-secondary);
  margin-top: 0.5rem;
}

.btn-primary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
  border: none;
  border-radius: 0.75rem;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(228, 130, 26, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  padding-bottom: 0.5rem;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: none;
  border-radius: 0.5rem 0.5rem 0 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: rgba(228, 130, 26, 0.1);
  color: var(--text-primary);
}

.tab-btn.active {
  background: rgba(228, 130, 26, 0.2);
  color: var(--text-primary);
  border-bottom: 2px solid #e4821a;
}

.tab-count {
  background: rgba(228, 130, 26, 0.3);
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
}

.blurred-tab {
  position: relative;
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
}

.blurred-tab::after {
  content: 'Coming Soon';
  position: absolute;
  font-size: 0.7rem;
  background: rgba(228, 130, 26, 0.9);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  top: -8px;
  right: -8px;
  white-space: nowrap;
}

/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
}

.loading-state {
  flex-direction: row;
  gap: 1rem;
  color: #e4821a;
}

.empty-icon {
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(228, 130, 26, 0.1);
  border-radius: 50%;
  margin-bottom: 1rem;
}

.empty-icon i {
  font-size: 2rem;
  color: #e4821a;
}

.empty-state h3 {
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0 0 1.5rem 0;
}

/* Users List */
.users-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.user-card {
  padding: 1.25rem;
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.2s;
}

.user-card:hover {
  border-color: rgba(228, 130, 26, 0.4);
  background: rgba(228, 130, 26, 0.05);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1rem;
}

.user-avatar.platform_admin {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
}

.user-avatar.platform_trainer {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

/* Fallback for old role names */
.user-avatar.admin {
  background: linear-gradient(135deg, #c83a77, #e44b55, #e4821a);
}

.user-avatar.trainer {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}

.user-details {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.user-email {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.user-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.25rem;
}

.role-badge {
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.role-badge.platform_admin {
  background: linear-gradient(135deg, rgba(200, 58, 119, 0.2), rgba(228, 130, 26, 0.2));
  color: #e4821a;
  border: 1px solid rgba(228, 130, 26, 0.3);
}

.role-badge.platform_trainer {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* Fallback for old role names */
.role-badge.admin {
  background: linear-gradient(135deg, rgba(200, 58, 119, 0.2), rgba(228, 130, 26, 0.2));
  color: #e4821a;
  border: 1px solid rgba(228, 130, 26, 0.3);
}

.role-badge.trainer {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

.last-login {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.assignments {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.assignments-label {
  font-size: 0.75rem;
  color: var(--text-muted);
}

.assignment-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.assignment-tag {
  padding: 0.125rem 0.5rem;
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 0.25rem;
  font-size: 0.7rem;
  color: #4ade80;
}

.user-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(228, 130, 26, 0.2);
  border-color: rgba(228, 130, 26, 0.5);
  color: var(--text-primary);
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.2);
  border-color: rgba(239, 68, 68, 0.5);
  color: #f87171;
}

.action-btn.success:hover {
  background: rgba(34, 197, 94, 0.2);
  border-color: rgba(34, 197, 94, 0.5);
  color: #4ade80;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.modal {
  background: var(--bg-card);
  border: 1px solid var(--accent-border);
  border-radius: 1rem;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.modal.modal-large {
  max-width: 640px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-header h2 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  border-radius: 0.5rem;
}

.modal-close:hover {
  background: var(--bg-card-alt);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.form-group input:focus {
  outline: none;
  border-color: rgba(228, 130, 26, 0.5);
}

.role-selector {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.role-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-secondary);
}

.role-option:hover {
  border-color: rgba(228, 130, 26, 0.3);
}

.role-option.selected {
  background: linear-gradient(135deg, rgba(228, 130, 26, 0.2), rgba(236, 72, 153, 0.1));
  border-color: rgba(228, 130, 26, 0.5);
  color: var(--text-primary);
}

.role-option i {
  font-size: 1.5rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.btn-secondary {
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
}

.btn-secondary:hover {
  background: var(--bg-card-alt);
}

/* Assignments Grid */
.assignments-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

.tenant-checkbox {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: var(--bg-card-alt);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
}

.tenant-checkbox:hover {
  border-color: rgba(228, 130, 26, 0.3);
}

.tenant-checkbox.checked {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.4);
}

.tenant-checkbox input {
  display: none;
}

.tenant-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.tenant-name {
  font-size: 0.875rem;
  color: var(--text-primary);
}

.tenant-slug {
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* Blur Overlays */
.blurred-section {
  position: relative;
  pointer-events: none;
}

.blur-overlay {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(4px);
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  z-index: 10;
  border-radius: 1rem;
  color: var(--text-primary);
  font-weight: 600;
}

.dark-theme .blur-overlay {
  background: rgba(42, 42, 36, 0.7);
}

.blur-overlay i {
  font-size: 2rem;
  color: #e4821a;
}

.blur-overlay span {
  font-size: 1rem;
}

.blur-overlay p {
  font-size: 0.875rem;
  font-weight: 400;
  color: var(--text-secondary);
  margin: 0;
}

.blur-overlay-small {
  position: absolute;
  inset: 0;
  backdrop-filter: blur(2px);
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  z-index: 10;
  border-radius: 0.5rem;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.75rem;
}

.dark-theme .blur-overlay-small {
  background: rgba(42, 42, 36, 0.6);
}

.blur-overlay-small i {
  font-size: 1rem;
  color: #e4821a;
}

.blurred-button {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed;
  position: relative;
}

.blurred-button::after {
  content: 'Coming Soon';
  position: absolute;
  font-size: 0.6rem;
  background: rgba(228, 130, 26, 0.9);
  color: white;
  padding: 2px 4px;
  border-radius: 3px;
  top: -6px;
  right: -6px;
  white-space: nowrap;
}
</style>
